/**
 * PeakPrioritiesLogo — the Peak Priorities logo (PP_Logo_v2): the shield mark and the
 * name in capitals. The current logo, confirmed by the owner 2026-10-01; it replaces
 * the earlier lockup in public/peakpriorities_logo.png.
 *
 * Inline so the name renders in Degular (an <img> cannot load the Typekit face) and so
 * the logo takes its color from the text color around it — ink on paper, paper on the
 * dark ground. That is why there is no separate reversed file.
 *
 * reuse-ack: mirrors marketing-site/src/components/peak/PeakLogo.astro — the site is a
 * separate Astro build and cannot import a React component; the path data is identical.
 */

interface PeakPrioritiesLogoProps {
  /** Rendered height in px. The logo is about 7.7 times as wide as it is tall. */
  height?: number;
  /** Show only the shield mark, without the name. */
  markOnly?: boolean;
  className?: string;
}

const MARK_PATHS = [
  'M56.69,93.74c-.66.38-1.32.59-2.26.85l-2.92-30.28h-9.98s9.18,31.45,9.18,31.45l-15.39,4.71L15.42,51.93c11.89-8.32,25.42-13.83,39.96-14.12,7.05-.1,13.85.94,20.26,3.75,4.28,1.88,9.83,5.41,10.67,9.9.29,1.54-.79,2.68-1.93,3.56-2.54,1.42-5.22,2.21-8.16,2.75-6.46,1.17-12.92,1.47-19.54,1.48v34.48Z',
  'M89.92,57.54c2.02-2.03,3.63-4.15,4.55-6.72,1.37-3.81.23-7.58-2.79-10.26-6.95-6.18-18.84-8.27-28.21-8.97-17.96-1-35.76,2.63-52.02,10.63L0,14.32,112.26,0l-23.86,84.21-26.95,8.24,5.38-24.33c5.18-1,10.02-2.51,14.69-4.8,3.07-1.59,5.79-3.38,8.39-5.77ZM62.95,18.14c0-3.56-2.88-6.44-6.44-6.44s-6.44,2.88-6.44,6.44,2.88,6.44,6.44,6.44,6.44-2.88,6.44-6.44ZM36.63,23.22c0-3.09-2.51-5.6-5.6-5.6s-5.6,2.51-5.6,5.6,2.51,5.6,5.6,5.6,5.6-2.51,5.6-5.6ZM85.97,23.23c0-3.09-2.5-5.59-5.59-5.59s-5.59,2.5-5.59,5.59,2.5,5.59,5.59,5.59,5.59-2.5,5.59-5.59Z',
];

export function PeakPrioritiesLogo({ height = 28, markOnly = false, className = '' }: PeakPrioritiesLogoProps) {
  return (
    <svg
      className={className}
      style={{ height, width: 'auto', display: 'block', overflow: 'visible' }}
      viewBox={markOnly ? '0 0 112.26 108.83' : '0 0 836.06 108.83'}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Peak Priorities"
    >
      <g fill="currentColor">
        {MARK_PATHS.map((d) => (
          <path key={d.slice(0, 24)} d={d} />
        ))}
        {!markOnly && (
          <text
            transform="translate(133.72 78.97)"
            style={{
              fontFamily: "'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif",
              fontSize: 96,
              fontWeight: 600,
              fontVariationSettings: "'opsz' 72, 'wght' 600",
            }}
          >
            <tspan letterSpacing=".04em" x="0" y="0">P</tspan>
            <tspan letterSpacing=".04em" x="51.17" y="0">E</tspan>
            <tspan letterSpacing=".03em" x="102.33" y="0">AK P</tspan>
            <tspan letterSpacing=".03em" x="284.06" y="0">R</tspan>
            <tspan letterSpacing=".04em" x="341.08" y="0">IO</tspan>
            <tspan letterSpacing=".03em" x="427.19" y="0">R</tspan>
            <tspan letterSpacing=".04em" x="484.22" y="0">ITIES</tspan>
          </text>
        )}
      </g>
    </svg>
  );
}

/** The slate the logo is drawn in on light grounds. */
export const PEAK_LOGO_SLATE = '#3D4B5C';
